import { useRef, useState } from "preact/hooks";

import { useModalA11y } from "../hooks/useModalA11y";
import { toISODate, todayLocal } from "../lib/calendarDates";

export interface AddWeightSheetProps {
  open: boolean;
  animalName: string;
  onAdd: (weightLb: number, weighedOn: string, note: string | null) => Promise<void>;
  onCancel: () => void;
}

/**
 * Sprint-010 C3 (design-phase-c.md §2.5) — the Weights tab's "+ Log a
 * weight" bottom sheet, admin/api only (the caller gates rendering on
 * `isAdmin`; see `AnimalDetailOverlay.tsx`). Same chrome/dismiss
 * conventions and synchronous-reset-on-open discipline as
 * `AddHealthNoteSheet` (sprint-005 C1's root-cause fix — see that
 * component's own docstring for the full incident writeup this pattern
 * exists to avoid repeating).
 *
 * Unlike `AddHealthNoteSheet`'s create flow, this sheet DOES show a Date
 * field on create — a deliberate, spec'd asymmetry (§2.4's own "why this
 * differs" note): a weight reading is more often jotted down in the field
 * and entered later that same evening, so the date genuinely needs to be
 * pickable at creation time.
 */
export function AddWeightSheet({ open, animalName, onAdd, onCancel }: AddWeightSheetProps) {
  const todayIso = toISODate(todayLocal());
  const [weightLb, setWeightLb] = useState("");
  const [weighedOn, setWeighedOn] = useState(todayIso);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const weightRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset SYNCHRONOUSLY on the closed->open transition — see
  // `AddHealthNoteSheet.tsx`'s docstring for why this must never be a
  // post-commit `useEffect`.
  const wasOpenRef = useRef(open);
  if (open && !wasOpenRef.current) {
    setWeightLb("");
    setWeighedOn(todayIso);
    setNote("");
    setError(null);
  }
  wasOpenRef.current = open;

  // Sprint-011 C1 (TD-012) — backdrop + Cancel route through the hook's
  // returned `close`, not `onCancel` directly.
  const { close } = useModalA11y(containerRef, {
    active: open,
    onClose: onCancel,
    initialFocusRef: weightRef,
  });

  if (!open) return null;

  const parsedWeight = Number(weightLb);
  const isValidWeight = weightLb.trim() !== "" && Number.isFinite(parsedWeight) && parsedWeight > 0;
  const canSave = isValidWeight && weighedOn !== "" && !saving;

  async function submit() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await onAdd(parsedWeight, weighedOn, note.trim() ? note.trim() : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save — try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div class="assign-sheet-backdrop is-open" onClick={close} />
      <div
        ref={containerRef}
        class="assign-sheet is-open"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-weight-title"
      >
        <div class="assign-sheet-handle" aria-hidden="true" />
        <h2 class="assign-sheet-title" id="add-weight-title">
          Log a weight for {animalName}
        </h2>

        {/* Sprint-019 E5b (audit row #17) — the "(lb)" suffix already on
         * this label is the unit confirmation the audit flagged as a
         * possible gap; UI Designer/Frontend confirm it already resolves
         * the concern (`weight_lb` is the app's one fixed unit) — no
         * additional hint/tooltip needed here, per the audit's own
         * "confirm before treating as a gap" caveat. */}
        <label class="admin-field-label" for="weight-lb-input">
          Weight (lb)
        </label>
        <input
          ref={weightRef}
          id="weight-lb-input"
          type="number"
          inputMode="decimal"
          min="0"
          step="0.1"
          class="pick-input"
          value={weightLb}
          required
          aria-required="true"
          onInput={(e) => setWeightLb((e.target as HTMLInputElement).value)}
        />

        <label class="admin-field-label" for="weight-date-input">
          Date
        </label>
        <input
          id="weight-date-input"
          type="date"
          class="pick-input"
          max={todayIso}
          value={weighedOn}
          required
          aria-required="true"
          onInput={(e) => setWeighedOn((e.target as HTMLInputElement).value)}
        />

        <label class="admin-field-label" for="weight-note-input">
          Note (optional)
        </label>
        <textarea
          id="weight-note-input"
          class="pick-input admin-textarea"
          value={note}
          onInput={(e) => setNote((e.target as HTMLTextAreaElement).value)}
        />

        {error ? (
          <p class="error-hint" role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          class="primary-btn"
          style={{ marginTop: "14px" }}
          disabled={!canSave}
          onClick={() => void submit()}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" class="secondary-btn" style={{ marginTop: "10px" }} onClick={close}>
          Cancel
        </button>
      </div>
    </>
  );
}
